import React from 'react';
import {
  User,
  Mail,
  Calendar,
  Settings,
  UserPlus,
  UserMinus,
  CheckCircle,
  XCircle,
  Building2,
  DollarSign,
  Shield
} from 'lucide-react';
import { EmployeeWithAssignments } from '../../types/employee';
import { formatDate } from '../../utils/dateUtils';

interface EmployeeCardProps {
  employee: EmployeeWithAssignments;
  onAssign: () => void;
  onUnassign: () => void;
}

export const EmployeeCard: React.FC<EmployeeCardProps> = ({
  employee,
  onAssign,
  onUnassign
}) => {
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-red-100 text-red-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'user':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
      case 'admin':
        return <Shield className="h-3 w-3" />;
      default:
        return <User className="h-3 w-3" />;
    }
  };

  const formatRoleDisplay = (role: string) => {
    return role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // R18 Task I-b: the distinct customers this salesman covers, derived from
  // his ACTIVE account assignments. One customer commonly spans several
  // accounts, so de-duplication is the whole point of showing it this way.
  // Assignments whose account has no client attached are skipped — there is
  // no customer to name.
  const coveredClients = Array.from(
    new Set(
      employee.user_accounts
        .filter((a) => a.is_active)
        .map((a) => a.account?.client?.client_name)
        .filter((name): name is string => Boolean(name))
    )
  ).sort();

  // Calculate total commission percentage across all assignments
  const totalCommissionPercentage = employee.user_accounts.reduce(
    (sum, account) => sum + parseFloat(account.commission_percentage.toString()),
    0
  );

  // Get active assignments count
  const activeAssignments = employee.user_accounts.filter(account => account.is_active).length;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-semibold text-lg">
                {employee.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{employee.name}</h3>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(employee.role)}`}>
                  {getRoleIcon(employee.role)}
                  <span className="ml-1">{formatRoleDisplay(employee.role)}</span>
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  employee.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {employee.is_active ? (
                    <>
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Active
                    </>
                  ) : (
                    <>
                      <XCircle className="h-3 w-3 mr-1" />
                      Inactive
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Action Menu */}
          <div className="flex items-center space-x-2">
            <button
              onClick={onAssign}
              className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              title="Assign to Accounts"
            >
              <UserPlus className="h-5 w-5" />
            </button>
            {employee.user_accounts.length > 0 && (
              <button
                onClick={onUnassign}
                className="p-2 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
                title="Unassign from Accounts"
              >
                <UserMinus className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Contact Info */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-gray-600">
            <Mail className="h-4 w-4 mr-2" />
            <span className="truncate">{employee.email}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="h-4 w-4 mr-2" />
            <span>Joined {formatDate(employee.created_at)}</span>
          </div>
          {employee.last_login_at && (
            <div className="flex items-center text-sm text-gray-600">
              <User className="h-4 w-4 mr-2" />
              <span>Last login {formatDate(employee.last_login_at)}</span>
            </div>
          )}
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Building2 className="h-4 w-4 text-gray-500 mr-1" />
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {employee.user_accounts_count}
            </div>
            <div className="text-xs text-gray-600">
              Accounts ({activeAssignments} active)
            </div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <DollarSign className="h-4 w-4 text-gray-500 mr-1" />
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {employee.commissions_count}
            </div>
            <div className="text-xs text-gray-600">Commissions</div>
          </div>
        </div>

        {/* Customers covered — R18 Task I-b. Peter (2026-10-08): "it shows a
            couple account assigned on the bubbles I really rather just see
            the clients ... I generally need to see the list of customers a
            sales guy has I just don't know where to find that."

            A customer is usually covered through several accounts (OASYS,
            CMTA, etc.), so the account list was both longer and less useful
            than the customer list. Individual accounts remain one click away
            in Manage Assignments for the "one off" case Peter described. */}
        {coveredClients.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Customers</h4>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {coveredClients.slice(0, 3).map((clientName) => (
                <div key={clientName} className="flex items-center space-x-2 text-sm">
                  <div className="w-2 h-2 rounded-full bg-green-400" />
                  <span className="text-gray-700 truncate">{clientName}</span>
                </div>
              ))}

              {coveredClients.length > 3 && (
                <div className="text-xs text-gray-500 text-center pt-1">
                  +{coveredClients.length - 3} more
                </div>
              )}
            </div>

            <a
              href={`/clients?user_id=${employee.id}`}
              className="mt-2 inline-block text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              View all customers
            </a>

            {totalCommissionPercentage > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-sm">
                <span className="text-gray-600">Total Commission:</span>
                <span className="font-semibold text-gray-900">
                  {totalCommissionPercentage.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        )}

        {/* No customers. Gated on coveredClients, not user_accounts: an
            employee whose assignments are all inactive, or whose accounts
            have no client attached, would otherwise render neither block and
            leave a blank card. */}
        {coveredClients.length === 0 && (
          <div className="border-t border-gray-200 pt-4">
            <div className="text-center py-4">
              <Settings className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-2">No customers assigned</p>
              <button
                onClick={onAssign}
                className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-600 text-sm rounded-lg hover:bg-blue-100 transition-colors"
              >
                <UserPlus className="h-3 w-3 mr-1" />
                Assign Now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};